import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale, height } from '@components/frame';
import { isPhone, isWeb, formatSinceTime } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import Line from '@components/common/line';
import EditableLine from '@components/common/editableLine';

import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { modalBg, modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

export default ({
  C,
  requestIpRangeScan,
  setMode,
  MODES,
  connectorIpRange,
  setSelectedRange,
  getConnectorIpRange,
  connectorIpScanStatus,
  ipScanStatus,
  ipScanUpdatedOn,
  maxHeight,
  configureNetworkScan,
  networkScanIsEnabled,
}) => {
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [automaticallySearchLoading, setAutomaticallySearchLoading] = useState(false);

  useEffect(() => {
    getConnectorIpRange();
    (async () => {
      await connectorIpScanStatus();
      setStatusLoaded(true);
    })();
  }, []);

  const submit = async () => {};

  const including = connectorIpRange.filter((i) => !i.isExclusion);
  const exclusion = connectorIpRange.filter((i) => i.isExclusion);

  const selectItem = (item, header) => {
    setSelectedRange({ ...item, header });
    setMode(MODES.IP_RANGE_DETAILS);
  };

  const createIPRange = (item, header) => {
    setSelectedRange({ ...item, header });
    setMode(MODES.CREATE_IP_RANGE);
  };

  const isSearching = ipScanStatus === 'InProgress';
  const headerHeight = scale(58);

  return (
    <View style={{}}>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: scale(58),
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
            <TouchableElement
              onPress={() => {
                setSelectedRange(null);
                setMode(MODES.SEARCH_MACHINES);
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15), flexDirection: 'row', alignItems: 'center' }]}>
                  <Svg icon="chevronLeft" width={scale(11, false)} height={scale(isWeb ? 19 : 17, false)} color="Link / Light / Primary" />
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')), marginLeft: scale(7) }, font.Body]}>{i18n.t('Back')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
          <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Search Settings')}</Text>
        </View>
      </View>
      <View style={{ maxHeight: maxHeight - headerHeight }}>
        <ScrollView>
          <View style={{ padding: scale(15) }}>
            <View>
              <EditableLine
                {...{
                  item: {
                    value: networkScanIsEnabled,
                    key: 'automaticallySearch',
                    name: 'Automatically Search',
                    noArrow: true,
                    isLable: true,
                    oneLine: true,
                    small: false,
                    width: 170,
                    type: 'boolean',
                    loading: automaticallySearchLoading,
                    onChange: async (v) => {
                      setAutomaticallySearchLoading(true);
                      await configureNetworkScan(v);
                      setAutomaticallySearchLoading(false);
                    },
                  },
                  index: 0,
                  array: [{}],
                  C,
                  topBorder: false,
                  bottomBorder: false,
                  roundTop: true,
                  roundBottom: true,
                  bg: modalFieldBg,
                }}
              />
            </View>
            {Boolean(including.length) && (
              <View style={{ marginTop: scale(21) }}>
                <View style={{ paddingHorizontal: scale(15), flexDirection: 'row' }}>
                  <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('MACHINE IP RANGES')}</Text>
                </View>
                <View style={{ marginTop: scale(5) }}>
                  {including.map((item, index) => (
                    <Line
                      key={index}
                      {...{
                        item: {
                          value: ' ',
                          name: `${item.startIpAddress} - ${item.endIpAddress}`,
                          onClick: () => selectItem(item, 'Machine IP Range'),
                        },
                        index,
                        array: including,
                        C,
                        roundTop: index === 0,
                        roundBottom: including.length - 1 === index,
                        takePhoneLayout: true,
                        bg: modalFieldBg,
                      }}
                    />
                  ))}
                </View>
                <View style={{ paddingHorizontal: scale(15), marginTop: scale(6), flexDirection: 'row' }}>
                  <Text style={[{ color: C('Label/Light/Tertiary') }, font['Caption 1']]}>
                    {i18n.t('The Smart Factory Connector will search in these IP ranges for machines it can connect to')}
                  </Text>
                </View>
              </View>
            )}

            <View style={{ marginTop: scale(20) }}>
              <TouchableElement
                onPress={() => {
                  createIPRange({ isExclusion: false }, 'Add Machine IP Range');
                }}
              >
                {({ boxStyle, pressHoverColor }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        height: scale(42),
                        width: '100%',
                        paddingHorizontal: scale(15),
                        justifyContent: 'center',
                        backgroundColor: C(pressHoverColor(modalFieldBg)),
                        borderRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                    ]}
                  >
                    <Text style={[{ color: C('Link/Light/Primary') }, font.Body]}>{i18n.t('Add Machine IP Range')}...</Text>
                  </View>
                )}
              </TouchableElement>
            </View>

            <View style={{ marginTop: scale(20) }}>
              <TouchableElement
                onPress={async () => {
                  if (statusLoaded) {
                    await requestIpRangeScan({
                      workerType: 'machines',
                    });
                  }
                }}
              >
                {({ boxStyle, pressHoverColor }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        height: scale(42),
                        width: '100%',
                        paddingHorizontal: scale(15),
                        justifyContent: 'center',
                        backgroundColor: C(pressHoverColor(modalFieldBg)),
                        borderRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                    ]}
                  >
                    <Text style={[{ color: C('Link/Light/Primary') }, font.Body]}>
                      {isSearching ? `${i18n.t('Restart Search')}` : i18n.t('Search Now')}
                    </Text>
                  </View>
                )}
              </TouchableElement>
            </View>
            <View style={{ paddingHorizontal: scale(15), marginTop: scale(6), flexDirection: 'row' }}>
              <Text style={[{ color: C('Label/Light/Tertiary') }, font['Caption 1']]}>{i18n.t('Search in all machine IP ranges')}</Text>
            </View>
            {ipScanUpdatedOn && (
              <View style={{ paddingHorizontal: scale(15), marginTop: scale(6), flexDirection: 'row' }}>
                <Text style={[{ color: C('Label/Light/Tertiary') }, font['Caption 1']]}>
                  {i18n.t('Last search')} {formatSinceTime(ipScanUpdatedOn)}
                </Text>
              </View>
            )}

            {Boolean(exclusion.length) && (
              <View style={{ marginTop: scale(21) }}>
                <View style={{ paddingHorizontal: scale(15), flexDirection: 'row' }}>
                  <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('DON’T SEARCH IN THESE IP RANGES')}</Text>
                </View>
                <View style={{ marginTop: scale(5) }}>
                  {exclusion.map((item, index) => (
                    <Line
                      key={index}
                      {...{
                        item: {
                          value: ' ',
                          name: `${item.startIpAddress} - ${item.endIpAddress}`,
                          onClick: () => selectItem(item, 'IP Exclusion Range'),
                        },
                        index,
                        array: exclusion,
                        C,
                        roundTop: index === 0,
                        roundBottom: exclusion.length - 1 === index,
                      }}
                    />
                  ))}
                </View>
                <View style={{ paddingHorizontal: scale(15), marginTop: scale(6), flexDirection: 'row' }}>
                  <Text style={[{ color: C('Label/Light/Tertiary') }, font['Caption 1']]}>
                    {i18n.t('The Smart Factory Connector will search NOT in these IP ranges for machines it can connect to')}
                  </Text>
                </View>
              </View>
            )}

            <View style={{ marginTop: scale(20) }}>
              <TouchableElement
                onPress={() => {
                  createIPRange({ isExclusion: true }, 'Add IP Exclusion Range');
                }}
              >
                {({ boxStyle, pressHoverColor }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        height: scale(42),
                        width: '100%',
                        paddingHorizontal: scale(15),
                        justifyContent: 'center',
                        backgroundColor: C(pressHoverColor(modalFieldBg)),
                        borderRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                    ]}
                  >
                    <Text style={[{ color: C('Link/Light/Primary') }, font.Body]}>{i18n.t('Add IP Exclusion Range')}...</Text>
                  </View>
                )}
              </TouchableElement>
            </View>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};
